import { createContext, useContext, useEffect, useState } from 'react';
import { captureError } from '@lingoda/monitor';
import { checkTime } from './logic';
import { useApiTime } from './hooks';
import { BrowserTimeSyncError } from './error';

type TimeCheckState = {
    timeCheckStatus: 'unknown' | 'synced' | 'unsynced';
};

export const TimeCheckContext = createContext<TimeCheckState | null>(null);

export const useTimeCheckContext = () => {
    const context = useContext(TimeCheckContext);
    if (context === null) {
        throw new Error('useTimeCheckContext must be used within a TimeCheckProvider');
    }

    return context;
};

export const TimeCheckProvider = ({ children }: React.PropsWithChildren<unknown>) => {
    const [timeCheckStatus, setTimeCheckStatus] = useState<TimeCheckState>({
        timeCheckStatus: 'unknown',
    });
    const { fetchApiTime } = useApiTime();

    useEffect(() => {
        checkTime(fetchApiTime)
            .then(() => setTimeCheckStatus({ timeCheckStatus: 'synced' }))
            .catch((error) => {
                captureError(error);

                if (error instanceof BrowserTimeSyncError) {
                    setTimeCheckStatus({ timeCheckStatus: 'unsynced' });
                } else {
                    setTimeCheckStatus({ timeCheckStatus: 'unknown' });
                }
            });
    }, [fetchApiTime]);

    return (
        <TimeCheckContext.Provider value={timeCheckStatus}>{children}</TimeCheckContext.Provider>
    );
};
